/** Who took part in a run, for a list cell or a detail line.

    The server already returns ``participants`` as "everyone who sent a message
    EXCEPT the starter", so nothing here subtracts it again - four surfaces each
    doing that themselves is how they drifted apart. A run with no starter is a
    scheduled pass: nobody opened it. */
export const runStarterLabel = (run: {
  started_by_email?: string;
}): string => run.started_by_email || "Automated";

export const runParticipantsLabel = (run: {
  participants?: string[];
}): string => (run.participants ?? []).join(", ");

/** The compact form for a narrow list column: who opened it, and how many
    others joined. */
export const runWhoLabel = (run: {
  started_by_email?: string;
  participants?: string[];
}): string => {
  const others = run.participants ?? [];
  const starter = runStarterLabel(run);
  return others.length > 0 ? `${starter} +${others.length}` : starter;
};

/** An agent key as its display label, falling back to the key.
 *
 * Roster lookups are tiny (five agents) so a linear scan is right; the point is
 * that every surface rendering an agent name uses the same fallback rather than
 * showing a raw key on one page and a label on another.
 */
export const agentLabelFrom =
  (roster: readonly { agent: string; label: string }[]) =>
  (agent: string): string =>
    roster.find((a) => a.agent === agent)?.label ?? agent;

/** An agent key as a display name: "design_readiness" -> "Design readiness".
 *
 * The roster's own label is better wherever the roster is loaded - use
 * ``agentLabelFrom`` there. This is for the surfaces that hold a key and no
 * roster (a live feed's step tags, the context gauge), which otherwise rendered
 * the raw key underscores and all.
 */
export const agentDisplayLabel = (agent: string): string => {
  const words = agent.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : agent;
};

export const MANAGER_AGENT = "manager";

// The agents who answered a turn: its participants, or the single answering agent for
// turns recorded without them.
export const agentsWho = (
  agent: string | null | undefined,
  participants?: readonly string[],
): string[] =>
  (participants ?? []).length ? [...(participants ?? [])] : agent ? [agent] : [];

/** What goes before the i-th of n names: "Manager, Grid and Legal". */
export const nameSeparator = (i: number, n: number): string =>
  i === 0 ? "" : i === n - 1 ? " and " : ", ";

// "Manager and Grid" for a delegated turn.
export const agentsLabel = (
  agent: string | null | undefined,
  participants?: readonly string[],
): string => {
  const who = agentsWho(agent, participants);
  if (who.length === 0) return "Agent";
  return who.map((a, i) => nameSeparator(i, who.length) + agentDisplayLabel(a)).join("");
};

